define((require) => {
    "use strict";

    const {
        extend,
        defaults,
        isBoolean
    }                                = require("underscore");
    const ActionSequence             = require("util/ActionSequence");
    const AddBreakpointAction        = require("ui/actions/AddBreakpointAction");
    const AddLocaleAction            = require("ui/actions/AddLocaleAction");
    const Backbone                   = require("backbone");
    const EditBehavior               = require("ui/behaviors/EditBehavior");
    const EditorEvents               = require("shared/services/EditorEvents");
    const ExperienceOptionsView      = require("ui/editor/ExperienceOptionsView");
    const GroupNavViewFactory        = require("editor/ui/experience/editor/GroupNavViewFactory");
    const LocaleCollection           = require("shared/services/localization/LocaleCollection");
    const LocaleSelectorControl      = require("editor/ui/experience/editor/LocaleSelectorControl");
    const NavBarView                 = require("ui/common/NavBarView");
    const NavigateToExperienceAction = require("ui/actions/NavigateToExperienceAction");
    const PublishableState           = require("util/PublishableState");
    const Syncer                     = require("shared/services/sync/Syncer");
    const template                   = require("ui/editor/EditorNavBarView.tpl");
    const Tooltip                    = require("ui/common/Tooltip");
    const ReactBridge                = require("backbone.react-bridge");
    const VariantComponent           = require("ui/editor/CollectionNavigation/VariantComponent").default;

    const TOOLTIPS = {
        "sync": {
            content: "Breakpoint Variants cannot be created when an Experience is being saved.",
            width: 250,
            placement: "right"
        },
        "breakpointEmailDisabled": {
            content: "Email mode does not support breakpoint variants. Disable Email Mode to create a breakpoint.",
            width: 300,
            placement: "right"
        }
    };

    return NavBarView.extend({

        template,
        templateHelpers() {
            return extend(this.__super(), {
                group: this._group,
                canConvertToGroup: this._canConvertToGroup,
                newDashboardExperience: this.model.get("id").startsWith("C3_")
            });
        },

        regions: () => defaults({
            c3CollectionsRegion: ".c3collection-region",
            groupNavViewRegion: ".variants-region",
            localeSelectorRegion: ".create-new-locale-variant",
            experienceOptionsPanel: ".experience-options-panel"
        }, NavBarView.prototype.regions),

        ui: () => defaults({
            navBar: ".nav-bar",
            c3CollectionsRegion: ".c3collection-region",
            experienceNav: ".experience-options-panel",
            variantsRegion: ".variants-region",
            createNewVariant: ".create-new-variant",
            createBreakpointGroup: ".add-breakpoint-variant",
            editorNavBar: ".editor-nav-bar"
        }, NavBarView.prototype.ui),

        events: () => defaults({
            "click @ui.createNewVariantOpen": "_preventDefault",
            "click @ui.createBreakpointGroup": "_createBreakpointGroupClickHandler"
        }, NavBarView.prototype.events),

        bindings: {
            ".experience-name": {
                observe: "name",
                updateMethod: "text",
                escape: false,
                events: ["blur"]
            }
        },

        behaviors: {
            editBehavior: {
                behaviorClass: EditBehavior,
                container: ".name-container",
                editFields: [{
                    input: ".experience-name",
                    save(value) {
                        this.model.set("name", value);

                        const eventProperties = {
                            experienceId: this.model.get("id"),
                            experienceVersion: this.model.get("version"),
                            experiencePushedVersion: this.model.get("pushedVersion"),
                            projectId: this.options.project && this.options.project.get("id"),
                            triggerOrigin: "EditExperienceNameInputField",
                            quickView: false,
                            updatedProperty: "name",
                            updatedValue: value
                        };

                        EditorEvents.trigger(EditorEvents.constants.UPDATE_EXPERIENCE, eventProperties);
                    }
                }]
            }
        },

        _group: null,
        _canConvertToGroup: null,

        _preventDefault(event) {
            event.preventDefault();
        },

        initialize() {
            this.__super();
            this._registerGroup(this.options.group);

            this._canCreateGroup();
            this.listenTo(this._currentPrincipalView, "navigation", (event) => {
                event.preventDefault();
                this._navigateTo(event.target.getAttribute("href"));
            });

            this.listenTo(this.model, "sync:state", (state, method) => {
                if (method !== "destroy") {
                    this._syncing = state === Syncer.State.Pending || state === Syncer.State.Syncing;
                    this.ui.createNewVariant.toggleClass("disabled", this._syncing);
                    this.ui.createBreakpointGroup.toggleClass("disabled", this._syncing);
                    this._updateTooltip(this.ui.createBreakpointGroup);
                }
            });
        },

        _disableBreakpointOnEmail() {
            const isEmailModeEnabled = this.viewScope.viewModel.get("experience").get("emailModeEnabled");
            this.ui.createBreakpointGroup.toggleClass("emailDisabled", isEmailModeEnabled);
            this._updateTooltip(this.ui.createBreakpointGroup);
        },

        _updateTooltip(ref) {
            const isEmailModeEnabled = this.viewScope.viewModel.get("experience").get("emailModeEnabled");
            let tooltipOptions;

            if (isEmailModeEnabled) {
                tooltipOptions = TOOLTIPS.breakpointEmailDisabled;
            } else if (this._syncing) {
                tooltipOptions = TOOLTIPS.sync;
            } else {
                Tooltip.remove(ref);
                return;
            }
            Tooltip.add(ref, tooltipOptions);
        },

        _registerGroup(group) {
            if (!group) {
                return;
            }

            this._group = group;

            this.listenToOnce(group, "destroy", () => {
                this._group = null;
                this._canCreateGroup();
                this.render();
                this.renderComponents();
            });
        },

        _canCreateGroup() {
            this._canConvertToGroup = !this._group && !this.model.hasAtLeastState(PublishableState.SCHEDULED); // SCHEDULED and PUBLISHED
        },

        onRender() {
            this.__super();
            this._toggleExperienceNav({visible: true});
            this.listenTo(this.viewScope.viewModel.get("experience"), "change:emailModeEnabled", this._disableBreakpointOnEmail);
        },

        onShow() {
            this.__super();
            this.renderComponents();
            this._disableBreakpointOnEmail();

            if (!this._canConvertToGroup) {
                Tooltip.add(this.ui.createBreakpointGroup, {
                    content: "Breakpoint Variants cannot be created when an Experience is " + this.model.get("state") + ".",
                    width: 200,
                    placement: "right"
                });
            }
        },

        // Renders sub
        renderComponents() {
            this._renderGroupNavbar();
            this.experienceOptionsPanel.show(new ExperienceOptionsView({
                model: this.model,
                group: this._group,
                project: this.options.project,
                companySettings: this.options.companySettings,
                viewModel: this.options.viewModel,
                integrationsCollection: this.options.integrationsCollection
            }));
        },

        _handleSave() {
            // trigger a save
            this.model.set({ state: Syncer.State.Pending });
            this.model.trigger("sync:state");
        },

        _handleCreateVariant(experienceCollection) {
            new ActionSequence()
                .add(new AddBreakpointAction(this._group, this.model, this.options.project, experienceCollection))
                .add(new NavigateToExperienceAction(this.model.get("sceneNumber")))
                .execute();
        },

        navigateToExperience(experienceId) {
            let sceneNumber = this.options.viewModel.get("sceneNumber");
            new NavigateToExperienceAction(sceneNumber)
                .execute(experienceId);
        },

        _handleChangeVariant(experienceId) {
            let eventProperties = {
                experienceId: experienceId,
                triggerOrigin: "VariantIcon",
                groupType: "BreakpointGroup"
            };
            EditorEvents.trigger(EditorEvents.constants.SWITCH_VARIANT, eventProperties);
            this.navigateToExperience(experienceId);
        },


        _renderGroupNavbar() {
            if (this._group) {
                // Render group controller
                this.ui.createNewVariant.hide();
                this.ui.navBar.toggleClass("narrow", this._group.getNumOfAssets() > 7);

                this.groupNavViewRegion.show(GroupNavViewFactory.create({
                    model:               this._group,
                    experience:          this.model,
                    viewModel:           this.viewScope.viewModel,
                    project:             this.options.project,
                    editorNavBarElement: this.ui.editorNavBar,
                    pushed:              this.options.pushed,
                    channelConfig:       this.options.channelConfig
                }));
            } else {

                if (this.model.get("id").startsWith("C3_")) {
                    this.c3CollectionsRegion.show((ReactBridge.viewFromComponent(VariantComponent, {
                        props: {
                            handleCreateVariant: this._handleCreateVariant.bind(this),
                            experience: this.model,
                            handleChangeVariant: this._handleChangeVariant.bind(this),
                            handleSave: this._handleSave.bind(this),
                        }
                    })));

                    // logic to disable the user to switch between variants/create variants while an experience
                    // is being saved to prevent multiple saves and maintain version consistency.
                    this.listenTo(this.model, "sync:state", (state) => {
                        if(state === Syncer.State.Synced) {
                            window.dispatchEvent(new Event("experienceSaved"));
                            document.getElementById("c3collections").style.pointerEvents = 'auto';
                            document.getElementById("c3CollectionsWrapper").style.cursor = 'auto';
                        } else {
                            document.getElementById("c3collections").style.pointerEvents = 'none';
                            document.getElementById("c3CollectionsWrapper").style.cursor = 'not-allowed';
                        }
                    });
                }

                // show locale selector
                this.localeSelectorRegion.show(new LocaleSelectorControl({
                    locales:       LocaleCollection.models,
                    firstVariant:  true,
                    selectHandler: this._createLocaleGroup.bind(this)
                }));
            }
        },

        _createBreakpointGroupClickHandler(event) {
            if (
                this.model.hasAtLeastState(PublishableState.SCHEDULED) ||
                this._syncing ||
                this.ui.createBreakpointGroup.hasClass("emailDisabled")) { // SCHEDULED and PUBLISHED and not EmailMode
                event.stopPropagation();
                event.preventDefault();
            } else {
                this._createBreakpointGroup();
            }
        },

        _isExperienceBarVisible() {
            return this.ui.experienceNav.hasClass("visible");
        },

        _toggleExperienceNav(options) {
            const isVisible = isBoolean(options.visible) ? options.visible : !this._isExperienceBarVisible();
            this.ui.experienceNav.toggleClass("visible", isVisible);
            this.ui.variantsRegion.toggleClass("visible", isVisible);
        },

        _navigateTo(path) {
            Backbone.history.navigate(path, true);
        },

        toggleProjectPanel() {
            this.__super();
            this._toggleExperienceNav({visible: !this._isExperienceBarVisible()});
        },

        _createLocaleGroup(language) {
            // We stay the same page when creating a localization group, but re-render the nav bar
            new AddLocaleAction(this._group, this.model, this.options.project, language)
                .execute()
                .done((group) => {
                    this._registerGroup(group);
                    this.render();
                    this.renderComponents();
                });
        },

        _createBreakpointGroup() {
            // We are always navigating to another variant, so no need to re-render anything
            new ActionSequence()
                .add(new AddBreakpointAction(this._group, this.model, this.options.project))
                .add(new NavigateToExperienceAction(this.model.get("sceneNumber")))
                .execute();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/EditorNavBarView.js